import { NonOptional } from '@insight-health-ai/roseate/utils';
import { Form, Input, Button, Modal, Select, Switch } from 'antd';
import { InsightTag } from 'src/components';
// import { useGetConditionList } from 'src/hooks/condition';
import {
  useCreateTag,
  useGetTagSettings,
  useUpdateTagDetails,
} from 'src/hooks/tag';
import { Tag } from 'src/models/tag';
import { showToastSuccess } from 'src/utilities/toast';

type TagEditFormProps = {
  open: boolean;
  onClose: (success?: boolean) => void;
  tag: Tag | NonOptional<Partial<Omit<Tag, 'id'>>, 'category'>;
};

export const TagEditForm = ({ open, onClose, tag }: TagEditFormProps) => {
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  const { mutateAsync: updateTagDetails, isPending } = useUpdateTagDetails();
  const { mutateAsync: createNewTag } = useCreateTag();
  // const { data: conditionList, isLoading: isLoadingConditionList } =
  //   useGetConditionList();

  const { data: tagSettings, isLoading: isLoadingTagSettings } =
    useGetTagSettings();

  const updateTag = async () => {
    const values = await form.validateFields();
    await updateTagDetails({
      id: (tag as Tag).id,
      name: values.name,
      description: values.description || '',
      instructions: values.instructions || '',
      condition_id: values.conditionId,
      meta: { ...tag?.meta, color: values.color },
      is_active: values.isActive ?? true,
    });
    showToastSuccess('Tag updated successfully!');
    onClose?.(true);
  };

  const createTag = async () => {
    const values = await form.validateFields();
    await createNewTag({
      category: tag.category,
      name: values.name,
      description: values.description || '',
      instructions: values.instructions || '',
      condition_id: values.conditionId,
      meta: { ...tag?.meta, color: values.color },
      is_active: values.isActive ?? true,
    });
    showToastSuccess('Tag created successfully!');
    onClose?.(true);
  };

  const renderSelectOption = (color: string) => {
    return (
      <div className="flex justify-between items-center gap-2">
        <InsightTag
          tag={{
            id: '',
            created_at: '',
            name: formValues?.name || 'Tag Name',
            meta: { color },
            category: 'tag',
          }}
          isSelected
        />
        <div
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>
    );
  };

  return (
    <Modal
      open={open}
      closable={!isPending}
      onCancel={() => onClose?.(false)}
      title={
        <div className="flex items-center gap-2 font-normal">
          <span className="text-md font-medium">
            {(tag as Tag).id ? 'Edit' : 'Add'} Tag
          </span>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2 mt-8">
          <Button
            disabled={isPending}
            className="ih-button-secondary"
            onClick={() => onClose?.(false)}
          >
            Cancel
          </Button>
          <Button
            loading={isPending}
            disabled={isPending}
            className="ih-button-primary"
            onClick={() => ((tag as Tag).id ? updateTag() : createTag())}
          >
            {(tag as Tag).id ? 'Update' : 'Create'}
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        initialValues={{
          name: tag.name,
          description: tag.description,
          instructions: tag.instructions,
          conditionId: tag.condition_id,
          color: tag.meta?.color,
          isActive: tag.is_active ?? true,
        }}
        layout="vertical"
      >
        <Form.Item
          className="field-wrapper"
          name="name"
          label={<span className="label">Name</span>}
          rules={[{ required: true }]}
        >
          <Input className="field" placeholder="Enter tag name" />
        </Form.Item>
        <Form.Item
          className="field-wrapper"
          name="description"
          label={<span className="label">Description</span>}
        >
          <Input className="field" placeholder="Enter tag description" />
        </Form.Item>
        <Form.Item
          className="field-wrapper"
          name="instructions"
          label={<span className="label">Instructions</span>}
        >
          <Input.TextArea
            autoSize={{ minRows: 4, maxRows: 10 }}
            className="field"
            placeholder="Enter tag instructions"
          />
        </Form.Item>
        {/* <Form.Item
          className="field-wrapper"
          name="conditionId"
          label={<span className="label">Condition</span>}
        >
          <Select
            className="field"
            showSearch
            allowClear
            placeholder="Select condition"
            filterOption={(input = '', option) => {
              return (
                String(option?.label)
                  ?.toLowerCase()
                  .includes(input.toLowerCase()) ||
                String(option?.value)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              );
            }}
            disabled={isLoadingConditionList}
            options={conditionList?.conditions.map((condition) => ({
              label: condition.condition_name,
              value: condition.condition_id,
            }))}
          />
        </Form.Item> */}
        <Form.Item
          className="field-wrapper"
          name="color"
          label={<span className="label">Color</span>}
          rules={[{ required: true }]}
        >
          <Select
            placeholder="Select tag color"
            className="field"
            disabled={isLoadingTagSettings}
            labelRender={({ value }) => renderSelectOption(value as string)}
            options={tagSettings?.allowed_colors.map((color) => ({
              label: renderSelectOption(color),
              value: color,
            }))}
          />
        </Form.Item>
        <Form.Item
          layout="horizontal"
          className="field-wrapper"
          name="isActive"
          label={<span className="label">Active</span>}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};
